import React, { FC, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import Icon from '../../../components/icon/Icon';
import authService from '../../../services/authService';
import commonService from '../../../services/commonService';
import showNotification from '../../../components/extras/showNotification';

const Login: FC = () => {
	const { setUser, setAuthUser } = useContext(AuthContext);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [commonLogoUrl, setCommonLogoUrl] = useState<string | null>(null);

	const navigate = useNavigate();

	useEffect(() => {
		let active = true;
		commonService
			.getAppAssetUrl('commonlogo')
			.then((url) => {
				if (active) setCommonLogoUrl(url);
			})
			.catch(() => undefined);
		return () => {
			active = false;
		};
	}, []);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginMobileNumber: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: { loginMobileNumber?: string; loginPassword?: string } = {};

			if (!values.loginMobileNumber) {
				errors.loginMobileNumber = 'Mobile number is required';
			} else if (values.loginMobileNumber.length !== 10) {
				errors.loginMobileNumber = 'Mobile number must be exactly 10 digits';
			}

			if (!values.loginPassword) {
				errors.loginPassword = 'Password is required';
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				const response = await authService.login({
					mobile: values.loginMobileNumber.replace(/\D/g, ''),
					password: values.loginPassword,
				});

				if (response?.data?.user) {
					if (setAuthUser) {
						setAuthUser(response.data.user);
					}
					if (setUser) {
						setUser(
							response.data.user.name ||
							response.data.user.mobile ||
							response.data.user.username ||
							'',
						);
					}
				}

				navigate('/');
			} catch (error: any) {
				const msg =
					error?.data?.message ||
					error?.message ||
					'Invalid mobile number or password. Please try again.';
				showNotification('Login Failed', msg, 'danger');
			} finally {
				setIsLoading(false);
			}
		},
	});

	return (
		<PageWrapper isProtected={false} isGuestOnly title='Login' className='bg-dark'>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div
						className='col-xl-5 col-lg-7 col-md-9 shadow-3d-container'
						style={{ maxWidth: '500px' }}>
						<Card className='shadow-3d-dark'>
							<CardBody className='p-4 p-md-5'>
								<div className='text-center mb-4'>
									<Link
										to='/'
										className='text-decoration-none text-dark fw-bold display-2'
										aria-label='Logo'>
										{commonLogoUrl && (
											<img
												src={commonLogoUrl}
												alt='Logo'
												style={{
													height: '90px',
													maxWidth: '320px',
													width: 'auto',
													objectFit: 'contain',
												}}
											/>
										)}
									</Link>
								</div>

								<div className='text-center h2 fw-bold mt-3 text-dark'>
									Welcome Back
								</div>
								<div className='text-center text-muted mb-4'>
									Sign in to your account
								</div>

								<form className='row g-4' onSubmit={formik.handleSubmit}>
									<div className='col-12'>
										<FormGroup
											id='loginMobileNumber'
											isFloating
											label='Mobile Number'>
											<Input
												type='tel'
												maxLength={10}
												autoComplete='tel'
												placeholder='Enter 10-digit mobile number'
												value={formik.values.loginMobileNumber}
												isTouched={formik.touched.loginMobileNumber}
												invalidFeedback={formik.errors.loginMobileNumber}
												isValid={formik.isValid}
												onChange={(e: any) => {
													const numeric = String(e?.target?.value || '').replace(/\D/g, '').slice(0, 10);
													formik.setFieldValue('loginMobileNumber', numeric);
												}}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
											/>
										</FormGroup>
									</div>

									<div className='col-12'>
										<div className='position-relative'>
											<FormGroup id='loginPassword' isFloating label='Password'>
												<Input
													type={showPassword ? 'text' : 'password'}
													autoComplete='current-password'
													value={formik.values.loginPassword}
													isTouched={formik.touched.loginPassword}
													invalidFeedback={formik.errors.loginPassword}
													isValid={formik.isValid}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													onFocus={() => {
														formik.setErrors({});
													}}
												/>
											</FormGroup>
											<button
												type='button'
												className='btn position-absolute top-50 end-0 translate-middle-y me-2 text-muted border-0 bg-transparent p-2 z-3 d-flex align-items-center'
												tabIndex={-1}
												aria-label='Toggle password visibility'
												onClick={() => setShowPassword(!showPassword)}>
												<Icon icon={showPassword ? 'VisibilityOff' : 'Visibility'} />
											</button>
										</div>
									</div>

									{/* <div className='col-12 text-end'>
										<Link
											to='/auth-pages/forgot-password'
											className='text-decoration-none fw-semibold small'
											style={{ color: '#0E5F98' }}>
											Forgot Password?
										</Link>
									</div> */}

									<div className='col-12'>
										<Button
											type='submit'
											color='primary'
											className='w-100 py-3 fs-5'
											isDisable={
												isLoading ||
												!formik.values.loginMobileNumber ||
												formik.values.loginMobileNumber.length !== 10 ||
												!formik.values.loginPassword
											}>
											{isLoading && <Spinner isSmall inButton isGrow />}
											Sign In
										</Button>
									</div>
								</form>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Login;
